import {
  PermissionFieldPath,
  PermissionsItem,
  PermissionsSection,
} from "@/pages/settings/roles/edit/role-edit.types.ts";
import { PermissionType } from "@/types/role.types.ts";

export type BulkPermissionType = PermissionType.none | PermissionType.view | PermissionType.edit;

const bulkPermissionOrder: BulkPermissionType[] = [
  PermissionType.none,
  PermissionType.view,
  PermissionType.edit,
];

export const getBulkPermissionOptions = (section: PermissionsSection): BulkPermissionType[] => {
  const availableOptions = new Set<BulkPermissionType>();

  const collectOptions = (item: PermissionsItem) => {
    item.availableOptions.forEach((option) => {
      if (bulkPermissionOrder.includes(option as BulkPermissionType)) {
        availableOptions.add(option as BulkPermissionType);
      }
    });
    item.children?.forEach(collectOptions);
  };

  section.groups.forEach((group) => group.items.forEach(collectOptions));

  return bulkPermissionOrder.filter((option) => availableOptions.has(option));
};

const getPermissionValue = (
  item: PermissionsItem,
  target: BulkPermissionType
): PermissionType | undefined => {
  if (item.availableOptions.includes(target)) return target;

  if (target === PermissionType.none && item.availableOptions.includes(PermissionType.view)) {
    return PermissionType.view;
  }

  if (target === PermissionType.edit && item.availableOptions.includes(PermissionType.view)) {
    return PermissionType.view;
  }

  return undefined;
};

export const getBulkPermissionChanges = (
  section: PermissionsSection,
  target: BulkPermissionType
) => {
  const changes = new Map<PermissionFieldPath, PermissionType>();

  const applyItem = (item: PermissionsItem) => {
    const value = getPermissionValue(item, target);
    if (value) changes.set(item.field, value);
    item.children?.forEach(applyItem);
  };

  section.groups.forEach((group) => {
    if (
      target === PermissionType.view &&
      group.hiddenWhenField &&
      group.hiddenWhenValue === PermissionType.edit
    ) {
      changes.set(group.hiddenWhenField, PermissionType.none);
    }

    group.items.forEach(applyItem);
  });

  return changes;
};

export const getDependentPermissionChanges = (
  sections: PermissionsSection[],
  field: PermissionFieldPath,
  value: PermissionType,
  getPermission: (field: PermissionFieldPath) => PermissionType | undefined
) => {
  const changes = new Map<PermissionFieldPath, PermissionType>();
  const getNextPermission = (requestedField: PermissionFieldPath) =>
    requestedField === field ? value : getPermission(requestedField);

  const collectChanges = (item: PermissionsItem) => {
    const dependencyState = item.dependency?.getState(getNextPermission);
    if (
      dependencyState?.value !== undefined &&
      dependencyState.value !== getNextPermission(item.field)
    ) {
      changes.set(item.field, dependencyState.value);
    }
    item.children?.forEach(collectChanges);
  };

  sections.forEach((section) =>
    section.groups.forEach((group) => group.items.forEach(collectChanges))
  );

  return changes;
};
